import React, { useRef, useEffect } from "react";

import { Text, Stack } from "@bookingcom/bui-react";
import { useI18n, t } from "@bookingcom/lingojs-react";
import PostBookingPaymentController from "../PostBookingPaymentController";
import Wrapper from "../../atoms/Wrapper";
// eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
import { useStore } from "../../../store";
import { assert } from "../../../utils/assert";
import ConfirmationTestingTools from "../ConfirmationInner/components/ConfirmationTestingTools";
import AncillaryBreakdown from "./AncillaryBreakdown";
import useEventTracking from "../../../hooks/useEventTracking";
import useGlobalContext from "../../../hooks/useGlobalContext";
import useFormatPrice from "../../../hooks/useFormatPrice";
import { useHistory } from "react-router-dom";
import PostBookingCheckoutFooter from "../PostBookingCheckoutFooter";
import styles from "./PostBookingPaymentInner.module.css";
import { UnifiedPriceBreakdownPostBookingPayment } from "../UnifiedPriceBreakdown";

type Props = {};

const PostBookingPaymentInner: React.FunctionComponent<Props> = () => {
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const store = useStore();
  const i18n = useI18n();
  const history = useHistory();
  const { formatPrice } = useFormatPrice();

  const { order } = store.order;
  assert(order, "No order");

  const { addOnOrder } = store.order;
  assert(addOnOrder, "No add-on order");

  const { ancillaries } = addOnOrder;
  assert(ancillaries, "No ancillaries");

  const { orderTerms, supplierInfo } = order;
  const confirmationFinePrintContent = supplierInfo?.content?.confirmationFinePrint;

  const { requestId } = useGlobalContext();
  const trackV2 = useEventTracking("pb_checkout_payment", requestId);
  const trackFasttrack = useEventTracking("fasttrack", requestId);
  const trackingRef = useRef({
    trackV2,
    ancillaries: addOnOrder.ancillaries
  });

  useEffect(() => {
    const { trackV2, ancillaries } = trackingRef.current;
    const checkedoutAncillaries = ancillaries && Object.keys(ancillaries);

    if (checkedoutAncillaries?.includes("checkedInBaggage") || checkedoutAncillaries?.includes("cabinBaggage")) {
      trackV2("show_pb_payments_page_luggage");
    }
    if (checkedoutAncillaries?.includes("seatMapSelection")) {
      trackV2("show_pb_payments_page_seat");
    }
    if (checkedoutAncillaries?.includes("fastTrack")) {
      trackV2("show_pb_payments_page_fasttrack");
      order && trackFasttrack("show_pb_payments_modal_fasttrack", { orderId: order.orderId });
    }
  }, [order, trackFasttrack]);

  return (
    <PostBookingPaymentController>
      {({ pcContainerRef, onPayClick, pcStatus, pcIsLoaded, getTotalPrice }) => {
        const totalPrice = getTotalPrice(ancillaries);
        return (
          <>
            <ConfirmationTestingTools />
            <Wrapper>
              <Stack gap={4} className={styles.marginBottomMedium}>
                <>
                  <Text variant="body_2">
                    {i18n.trans(t("flights_apps_step_generic_screen_title", { variables: { step: 2, step_total: 2 } }))}
                  </Text>
                  <Text variant="headline_2">{i18n.trans(t("flights_checkout_review_header"))}</Text>
                </>
                <Text variant="strong_1">{i18n.trans(t("flights_ancillary_confirmation_summary_extras"))}</Text>
              </Stack>
              <AncillaryBreakdown ancillaries={ancillaries} />
            </Wrapper>
            <Wrapper>
              <Text variant="headline_3" tagName="h2">
                {i18n.trans(t("flights_price_details_title"))}
              </Text>
              <UnifiedPriceBreakdownPostBookingPayment order={order} />
            </Wrapper>
            <Wrapper noBorder>
              <Stack gap={4}>
                <Text variant="strong_1">{i18n.trans(t("flights_checkout_payment"))}</Text>
                <div ref={pcContainerRef} />
              </Stack>
            </Wrapper>
            {orderTerms && confirmationFinePrintContent && (
              <Wrapper noBorder>
                <Text variant="strong_2">{i18n.trans(t("flights_checkout_conditions"))}</Text>
                <Text variant="small_1">
                  <span dangerouslySetInnerHTML={{ __html: confirmationFinePrintContent }} />
                </Text>
              </Wrapper>
            )}
            <PostBookingCheckoutFooter
              labelPrimary={`${i18n.trans(t("flights_checkout_complete_action"))} ${formatPrice(totalPrice.total)}`}
              onClickPrimary={() => {
                trackV2("click_pb_ancillary_pay_now", {
                  addon_order_id: addOnOrder.addOnOrderId,
                  ancillaries: Object.keys(addOnOrder.ancillaries || [])
                });
                onPayClick();
              }}
              isPrimaryEnabled={pcIsLoaded}
              isPrimaryLoading={pcStatus === "processing"}
              labelSecondary={i18n.trans(t("flights_action_back"))}
              onClickSecondary={() => {
                history.goBack();
              }}
            />
          </>
        );
      }}
    </PostBookingPaymentController>
  );
};
export default PostBookingPaymentInner;
